//import React from 'react';
import type { UserRole } from '../types/navigation';
import { NAV_PATHS } from '../types/navigation';
import type { SidebarItemProps } from '../components/molecues/SidebarItem';
//import { Badge } from '../components/atoms';

/**
 * Navigation Configuration
 * 
 * Defines the navigation structure for the application with role-based access control
 */

export interface NavigationConfig {
  title?: string;
  items: SidebarItemProps[];
  allowedRoles?: UserRole[];
}

/**
 * Check if user has access to a navigation item based on their role
 */
export const hasAccess = (
  allowedRoles: UserRole[] | undefined,
  userRole: UserRole | undefined
): boolean => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
};

/**
 * Filter navigation items based on user role
 */
export const filterNavigationByRole = (
  sections: NavigationConfig[],
  userRole: UserRole | undefined
): NavigationConfig[] => {
  return sections
    .filter(section => hasAccess(section.allowedRoles, userRole))
    .map(section => ({
      ...section,
      items: filterItemsByRole(section.items, userRole),
    }))
    .filter(section => section.items.length > 0);
};

const filterItemsByRole = (
  items: SidebarItemProps[],
  userRole: UserRole | undefined
): SidebarItemProps[] => {
  return items
    .filter(item => !item.children || hasAccess(undefined, userRole))
    .map(item => {
      if (item.children) {
        return {
          ...item,
          children: filterItemsByRole(item.children, userRole),
        };
      }
      return item;
    })
    .filter(item => !item.children || item.children.length > 0);
};

/**
 * Default Navigation Configuration
 */
export const getNavigationConfig = (): NavigationConfig[] => {
  return [
    // Main Navigation
    {
      items: [
        {
          label: 'Home',
          path: NAV_PATHS.HOME,
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
          ),
        },
        {
          label: 'Dashboards',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
          ),
          children: [
            {
              label: 'Analytics',
              path: '/dashboard/analytics',
              icon: <span className="text-[#EAB308] text-lg">A</span>,
            },
            // {
            //   label: 'Smart Home',
            //   path: '/dashboard/smart-home',
            //   icon: <span className="text-gray-400 text-lg">S</span>,
            // },
          ],
        },
      ],
      allowedRoles: [
        'SUPER_ADMIN',
        'CONTENT_MANAGER',
        'USER_SUPPORT',
        'ANALYST',
        'MODERATOR',
        // legacy / existing
        'ADMIN',
        'MERCHANT',
        'RESTAURANT_OWNER',
      ],
    },
    // Pages Section
    {
      title: 'PAGES',
      items: [
        {
          label: 'Music Library',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.97 4.97 0 005 14c-1.11 0-2.08.402-2.599 1M12 14a4.97 4.97 0 00-1.404-2.866M12 14v6m0 0l-3-3m3 3l3-3m-6-6V5a1 1 0 011-1h10a1 1 0 011 1v1" />
            </svg>
          ),
          children: [
            { label: 'Songs / Tracks', path: NAV_PATHS.SONGS },
            { label: 'Albums', path: NAV_PATHS.ALBUMS },
            { label: 'Artists', path: NAV_PATHS.ARTISTS },
            { label: 'Music Videos', path: '/pages/music-videos' },
            {
              label: 'Taxonomy',
              children: [
                { label: 'Languages', path: '/pages/applications/kanban' },
                { label: 'Genres', path: '/pages/applications/data-tables' },
              ],
            },
          ],
        },
        {
          label: 'Promotions',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
          ),
          children: [
            { label: 'Banners', path: NAV_PATHS.BANNERS },
            { label: 'Splash Screens', path: NAV_PATHS.SPLASH_SCREENS },
          ],
        },
        // {
        //   label: 'Authentication',
        //   icon: (
        //     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        //       <path
        //         fillRule="evenodd"
        //         d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        //         clipRule="evenodd"
        //       />
        //     </svg>
        //   ),
        //   children: [
        //     {
        //       label: 'Sign In',
        //       path: '/auth/login',
        //     },
        //     {
        //       label: 'Sign Up',
        //       path: '/auth/register',
        //     },
        //   ],
        // },
        // {
        //   label: 'Error',
        //   icon: <span className="text-white text-lg">E</span>,
        //   children: [
        //     {
        //       label: 'Error 404',
        //       path: '/error/404',
        //     },
        //     {
        //       label: 'Error 500',
        //       path: '/error/500',
        //     },
        //   ],
        // },
      ],
      allowedRoles:  [
        'SUPER_ADMIN',
        'CONTENT_MANAGER',
        // legacy
      ], // Available to all roles
    },
    // Admin Only Section
    {
      title: 'ADMIN',
      items: [
        {
          label: 'User Management',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          ),
          path: '/admin/users',
        },
        {
          label: 'TV User Onboarding',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          ),
          path: '/admin/users/tv-onboard',
        },
        {
          label: 'Subscription Plans',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
            </svg>
          ),
          path: '/admin/subscription-plans',
        },
        {
          label: 'Settings',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
          ),
          path: '/admin/settings',
        },
        {
          label: 'Payment Methods',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h8a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 7a2 2 0 00-2 2v3a2 2 0 002 2h8a2 2 0 002-2v-3a2 2 0 00-2-2H6zm1.5 2.5a.75.75 0 000 1.5h5a.75.75 0 000-1.5h-5z"
                clipRule="evenodd"
              />
            </svg>
          ),
          path: NAV_PATHS.PAYMENT_METHODS,
        },
      ],
      allowedRoles: [
        'SUPER_ADMIN',
        'USER_SUPPORT',
        // legacy
        'ADMIN',
      ],
    },
    // Content management admin section
    {
      title: 'CONTENT',
      items: [
        {
          label: 'App Updates',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 6a1 1 0 011-1h3v7a1 1 0 11-2 0v-4H6a1 1 0 01-1-1zm7-1a1 1 0 000 2h1v4a1 1 0 102 0v-4h1a1 1 0 100-2h-4z"
                clipRule="evenodd"
              />
            </svg>
          ),
          path: '/admin/app-updates',
        },
      ],
      allowedRoles: [
        'SUPER_ADMIN',
        'CONTENT_MANAGER',
        // legacy
        'ADMIN',
      ],
    },
    // Merchant Section
    {
      title: 'MERCHANT',
      items: [
        {
          label: 'My Products',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z"
                clipRule="evenodd"
              />
            </svg>
          ),
          path: '/merchant/products',
        },
        {
          label: 'Orders',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
          ),
          path: '/merchant/orders',
        },
      ],
      allowedRoles: ['MERCHANT', 'RESTAURANT_OWNER'],
    },
    // Documentation Section
    // {
    //   title: 'DOCS',
    //   items: [
    //     {
    //       label: 'Basic',
    //       icon: (
    //         <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    //           <path
    //             fillRule="evenodd"
    //             d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
    //             clipRule="evenodd"
    //           />
    //         </svg>
    //       ),
    //       path: '/docs/basic',
    //     },
    //     {
    //       label: 'Components',
    //       icon: (
    //         <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    //           <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
    //         </svg>
    //       ),
    //       path: '/docs/components',
    //       badge: <Badge variant="primary" size="sm">NEW</Badge>,
    //     },
    //     {
    //       label: 'Changelog',
    //       icon: (
    //         <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    //           <path
    //             fillRule="evenodd"
    //             d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
    //             clipRule="evenodd"
    //           />
    //         </svg>
    //       ),
    //       path: '/docs/changelog',
    //     },
    //   ],
    //   allowedRoles: undefined, // Available to all roles
    // },
  ];
};

